'use client';
import type { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { Button } from '@shared/components/ui/button';
import { MultiSelect } from '@shared/components/ui/MultiSelect';
import { cn } from '@shared/utils/utils';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { usePaymentMethods } from '../../hooks/usePaymentMethods';
import { useShopsFilters } from '../../hooks/useShopsFilters';

interface ShopsFiltersPanelProps {
  initialConditions?: FilterConditionOutput[];
  onChange?: (conditions: FilterConditionOutput[]) => void;
  className?: string;
}

export const ShopsFiltersPanel = ({ initialConditions = [], onChange, className }: ShopsFiltersPanelProps) => {
  const { t } = useTranslation('shopsModule');
  const { paymentMethods, loading: paymentMethodsLoading } = usePaymentMethods();

  const {
    filters,
    handleChannelChange,
    handleActiveChange,
    handleFulfillmentChange,
    handlePaymentMethodChange,
    clearAllFilters,
  } = useShopsFilters({ initialConditions, onChange });

  const channelOptions = [
    { value: '1', label: t('filters.channels.shopify') },
    { value: '3', label: t('filters.channels.woocommerce') },
    { value: '4', label: t('filters.channels.manual') },
    { value: '6', label: t('filters.channels.amazonFbm') },
  ];

  const activeOptions = [
    { value: '1', label: t('filters.active_yes') },
    { value: '0', label: t('filters.active_no') },
  ];

  const fulfillmentOptions = [
    { value: '1', label: t('filters.fulfillment_yes') },
    { value: '0', label: t('filters.fulfillment_no') },
  ];

  const paymentMethodOptions = useMemo(
    () =>
      paymentMethods.map((m) => ({
        value: m.id,
        label: m.name,
      })),
    [paymentMethods],
  );

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.mic_channel_id.length > 0) count++;
    if (filters.active.length > 0) count++;
    if (filters.fulfillment.length > 0) count++;
    if (filters.default_payment_method_id.length > 0) count++;
    return count;
  }, [filters]);

  return (
    <div className={cn('space-y-5', className)}>
      <FilterSection title={t('filters.title_section', 'Filtros')}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FilterField label={t('filters.mic_channel_id')}>
            <MultiSelect
              options={channelOptions}
              value={filters.mic_channel_id}
              onValueChange={handleChannelChange}
              placeholder={t('filters.all_channels', 'Todos los canales')}
              emptyText={t('filters.notFound', 'Sin resultados')}
              dataTestId="filter-channel"
              triggerClassName="h-[38px]"
            />
          </FilterField>

          <FilterField label={t('filters.active')}>
            <MultiSelect
              options={activeOptions}
              value={filters.active}
              onValueChange={handleActiveChange}
              placeholder={t('filters.all_statuses', 'Todos')}
              emptyText={t('filters.notFound', 'Sin resultados')}
              dataTestId="filter-active"
              triggerClassName="h-[38px]"
            />
          </FilterField>

          <FilterField label={t('filters.fulfillment')}>
            <MultiSelect
              options={fulfillmentOptions}
              value={filters.fulfillment}
              onValueChange={handleFulfillmentChange}
              placeholder={t('filters.all_statuses', 'Todos')}
              emptyText={t('filters.notFound', 'Sin resultados')}
              dataTestId="filter-fulfillment"
              triggerClassName="h-[38px]"
            />
          </FilterField>

          <FilterField label={t('filters.default_payment_method_id')}>
            <MultiSelect
              options={paymentMethodOptions}
              value={filters.default_payment_method_id}
              onValueChange={handlePaymentMethodChange}
              placeholder={
                paymentMethodsLoading ? t('common.loading') : t('filters.all_payment_methods', 'Todos los métodos')
              }
              emptyText={t('filters.notFound', 'Sin resultados')}
              disabled={paymentMethodsLoading}
              dataTestId="filter-payment-method"
              triggerClassName="h-[38px]"
            />
          </FilterField>
        </div>
      </FilterSection>

      {activeFiltersCount > 0 && (
        <div className="flex items-center justify-end pt-3 border-t border-card-border">
          <Button
            variant="ghost"
            size="sm"
            className="text-sm text-muted-foreground hover:text-foreground"
            onClick={clearAllFilters}
            type="button"
          >
            Limpiar {activeFiltersCount} {activeFiltersCount === 1 ? 'filtro' : 'filtros'}
          </Button>
        </div>
      )}
    </div>
  );
};

const FilterSection = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="space-y-3">{children}</div>
);

const FilterField = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="space-y-1.5">
    <label className="text-xs font-medium text-muted-foreground">{label}</label>
    {children}
  </div>
);
